import React, { useState } from 'react'

import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { t } from 'ttag'
import styled from 'styled-components/macro'
import 'react-dropdown/style.css'
import NutsModal from 'components/modals/NutsModal'
import { getRegionsAsList, updateSelectedFilters } from 'store/actions/filters'
import { NUTS_LEVEL_COLOR_MAPPING, SELECTED_FILTER_MAX_LINES } from 'store/constants/constants'
import Button from 'components/ui/Button'
import FilterTitle from 'components/ui/FilterTitle'
import SearchFilter from 'components/ui/SearchFilter'
import SelectedFilter from 'components/ui/SelectedFilter'
import FilterText from 'components/ui/FilterText'
import RemoveIcon from 'components/ui/RemoveIcon'
import SelectedFilterContainer from 'components/ui/SelectedFilterContainer'
import Dotdotdot from 'react-dotdotdot'
import LoadingSpinner from 'components/ui/LoadingSpinner'
import CustomRadioButton from 'components/ui/CustomRadioButton'
import CustomDropDownTreeSelect from 'components/ui/CustomDropdownTreeSelect'
import breakpoints from 'assets/stylesheet'
import latinize from 'latinize'
import NutsDropdown from 'components/filters/NutsDropdown'

const NutsSelectedFilter = styled(SelectedFilter)`
  background-color: ${props => NUTS_LEVEL_COLOR_MAPPING[props.level]};
`
const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  @media (max-width: ${breakpoints.bpTabletPx}) {
    display: blocK;
  }
`
const MapButton = styled(Button)`
  font-size: 12px;
  width: auto;
  padding: 0px 6px;
  margin-top: 10px;
  margin-left: 0;
  @media (max-width: ${breakpoints.bpTabletPx}) {
    width: 100%;
  }
`

const MarkerIcon = styled.i`
  margin-left: 10px;
`
const SpinnerContainer = styled.div`
  min-height: 40px;
  position: relative;
`
const NutsSection = styled.div`
  margin-top: 20px;
`
const NutsSectionTitle = styled.b`
  font-size: 16px;
  margin-bottom: 5px;
  margin-top: 17px;
  display: block;
  margin-left: 4px;
`
const StyledCustomRadioButton = styled(CustomRadioButton)`
  display: inline-block;
  margin-right: 20px;
  width: 100%;
`
const Input = styled.input`
  margin-right: 2px;
`
const Label = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: #4a4a4a !important;
  padding-left: 24px !important;
`

function MultiNutsSelector({ type, values, filterData, title, onClose, showFilter, customTop }) {
  const filters = useSelector(state => state.filters)
  const { currentSearchable, regions, selectedFilters } = filters
  const filterObject = selectedFilters[currentSearchable][type]
  const regionsByStrategy = filters.availableFilters.projects.regions_by_strategy

  return (
    <PureMultiNutsSelector
      type={type}
      values={values}
      filterData={filterData}
      title={title}
      onClose={onClose}
      showFilter={showFilter}
      customTop={customTop}
      regions={regions}
      regionsByStrategy={regionsByStrategy}
      filterObject={filterObject}
    />
  )
}

const handleNutsName = nuts =>
  nuts.english_alias !== null && nuts.english_alias !== ''
    ? `${latinize(nuts.english_alias)} / ${nuts.name}`
    : nuts.name

const getOptions = (regions, selectedOptions) =>
  regions.map(option => {
    const parentOption = {
      label: `[${option.nuts_id}] ${handleNutsName(option)}`,
      value: option.nuts_id,
      type: option.type
    }
    const selectedOption = selectedOptions.filter(s => s.id === option.nuts_id)
    if (selectedOption.length > 0) {
      parentOption.checked = true
    }
    return parentOption
  })

const PureMultiNutsSelector = React.memo(function PureMultiNutsSelector({
  type,
  values,
  filterData,
  title,
  onClose,
  showFilter,
  customTop,
  regions,
  regionsByStrategy,
  filterObject
}) {
  const dispatch = useDispatch()

  const selectedLeadPartners = filterObject[values[0]]
  const selectedPartners = filterObject[values[1]]
  const selectedSearchType = filterObject[values[2]]

  const leadPartnerOptions = getOptions(regions || [], selectedLeadPartners)
  const partnerOptions = getOptions(regions || [], selectedPartners)

  const [nutsLeadPartnerModalOpen, setNutsLeadPartnerModalOpen] = useState(false)
  const [nutsPartnerModalOpen, setNutsPartnerModalOpen] = useState(false)

  function onChange(currentNode, selectedNodes, filterId) {
    let filterValue = selectedNodes.map(node => ({
      id: node.value,
      label: node.label,
      paths: node.paths,
      type: node.type,
      filterType: filterId
    }))

    let data = {
      filterType: type,
      filterId,
      filterValue
    }

    dispatch(updateSelectedFilters(data))

    if (selectedSearchType === 'both') {
      filterValue = selectedNodes.map(node => ({
        id: node.value,
        label: node.label,
        paths: node.paths,
        type: node.type,
        filterType: values[1]
      }))

      data = {
        filterType: type,
        filterId: values[1],
        filterValue
      }

      dispatch(updateSelectedFilters(data))
    }
  }

  function handleOptionChange(changeEvent) {
    const data = {
      filterType: type,
      filterId: values[2],
      filterValue: changeEvent.target.value
    }
    dispatch(updateSelectedFilters(data))
  }

  function setNutsFilter(nuts, filterId) {
    let filterValue = []

    filterValue = nuts.map(nut => ({
      ...nut,
      filterType: filterId
    }))

    let data = {
      filterType: type,
      filterId,
      filterValue
    }

    dispatch(updateSelectedFilters(data))

    if (selectedSearchType === 'both') {
      filterValue = nuts.map(nut => ({
        ...nut,
        filterType: values[1]
      }))

      data = {
        filterType: type,
        filterId: values[1],
        filterValue
      }

      dispatch(updateSelectedFilters(data))
    }
  }

  function updateSelectedNuts(fetchedNuts, filterId) {
    const filterValue = fetchedNuts.map(nut => ({
      id: nut.nuts_id,
      label: nut.name,
      paths: nut.paths,
      type: nut.type
    }))

    const data = {
      filterType: type,
      filterId,
      filterValue
    }
    dispatch(updateSelectedFilters(data))
  }

  function toggleNutsModal(filterId) {
    if (filterId === values[0]) {
      setNutsLeadPartnerModalOpen(prevState => !prevState)
    } else if (filterId === values[1]) {
      setNutsPartnerModalOpen(prevState => !prevState)
    }
  }

  function fetchSelectedNuts(selectedNuts, filterId) {
    const noPaths = selectedNuts.filter(value => !value.paths)

    toggleNutsModal(filterId)
    if (noPaths.length > 0) {
      const nutsIds = {}
      nutsIds.nuts_ids = []
      selectedNuts.forEach(nut => {
        nutsIds.nuts_ids.push(nut.id)
      })
      dispatch(getRegionsAsList(nutsIds)).then(response => {
        updateSelectedNuts(response.value, filterId)
      })
    }
  }

  function onAreaRemove(e, filterId) {
    const filterValue = filterObject[filterId]
    const selectedAreas = filterObject.selectedAreas[filterId].filter(a => a !== e.value)

    const allFilters = []

    if (selectedAreas.length > 0) {
      for (let i = 0; i < selectedAreas.length; i++) {
        const selectedArea = selectedAreas[i]

        filterValue.forEach(filterItem => {
          if (regionsByStrategy[selectedArea].map(x => x.nuts_id).indexOf(filterItem.id) !== -1) {
            allFilters.push(filterItem)
          }
        })
      }
    }

    const data = {
      filterType: type,
      filterId,
      filterValue: allFilters
    }
    dispatch(updateSelectedFilters(data))
    const areaData = {
      filterType: type,
      filterId: 'selectedAreas',
      filterValue: {
        ...filterObject.selectedAreas,
        [filterId]: selectedAreas
      }
    }
    dispatch(updateSelectedFilters(areaData))
  }

  function handleFilterRemove(filterId, filterValue, valueIsArray = false) {
    let resultingValue = null
    if (valueIsArray) {
      resultingValue = filterObject[filterId].filter(
        s => s.id !== filterValue.id || s !== filterValue
      )
    }
    if ((filterObject.selectedAreas[filterId] || []).includes(filterValue.id)) {
      onAreaRemove({ value: filterValue.id }, filterId)
      return
    }
    const data = {
      filterType: type,
      filterId,
      filterValue: resultingValue
    }
    dispatch(updateSelectedFilters(data))
  }

  function renderSelectedValues() {
    return values.map(value => {
      if (filterObject[value]) {
        const filterValue = filterObject[value]
        if (filterValue instanceof Array) {
          let displayValues = [...filterValue]
          const selectedAreas = (filterObject.selectedAreas || {})[value] || []
          selectedAreas.forEach(a => {
            regionsByStrategy[a].forEach(r => {
              displayValues = displayValues.filter(d => d.id !== r.nuts_id)
            })
            displayValues.push({
              id: a,
              label: a.toUpperCase(),
              type: 0,
              filterId: a.filterId,
              filterType: value
            })
          })
          return displayValues.map(v => (
            <SelectedFilterContainer className="selected-filters" key={v.id}>
              <NutsSelectedFilter level={v.type}>
                <FilterText title={`${v.filterId} ${v.label}`}>
                  <Dotdotdot clamp={SELECTED_FILTER_MAX_LINES}>
                    <strong>
                      {v.filterType === 'nuts_lead' ? 'Lead partner ' : 'Project partner '}
                    </strong>
                    {v.label}
                  </Dotdotdot>
                </FilterText>
                <RemoveIcon
                  className="far fa-times"
                  onClick={() => handleFilterRemove(value, v, true)}
                />
              </NutsSelectedFilter>
            </SelectedFilterContainer>
          ))
        }
      }
      return false
    })
  }

  return (
    <>
      <SearchFilter open={showFilter} customTop={customTop}>
        <FilterTitle>{title}</FilterTitle>
        {!filterData && !regions ? (
          <SpinnerContainer>
            <LoadingSpinner showText={false} />
          </SpinnerContainer>
        ) : (
          <>
            <NutsSection>
              <NutsSectionTitle>{t`Selecting countries/regions of:`}</NutsSectionTitle>
              <StyledCustomRadioButton>
                <Input
                  type="radio"
                  name="nuts_both_option"
                  id="nuts_leads_and_projects_option"
                  value="both"
                  checked={selectedSearchType === 'both'}
                  onChange={handleOptionChange}
                />
                <Label htmlFor="nuts_leads_and_projects_option">{t`Both lead and project partners`}</Label>
              </StyledCustomRadioButton>
              <StyledCustomRadioButton>
                <Input
                  type="radio"
                  name="nuts_or_option"
                  id="nuts_leads_option"
                  value="leads"
                  checked={selectedSearchType === 'leads'}
                  onChange={handleOptionChange}
                />
                <Label htmlFor="nuts_leads_option">{t`Lead partners`}</Label>
              </StyledCustomRadioButton>
              <StyledCustomRadioButton>
                <Input
                  type="radio"
                  name="nuts_or_option"
                  id="nuts_projects_option"
                  value="projects"
                  checked={selectedSearchType === 'projects'}
                  onChange={handleOptionChange}
                />
                <Label htmlFor="nuts_projects_option">{t`Project partners`}</Label>
              </StyledCustomRadioButton>
            </NutsSection>
            <NutsSection>
              <CustomDropDownTreeSelect
                maxHeight="100px"
                data={selectedSearchType === 'projects' ? partnerOptions : leadPartnerOptions}
                texts={{
                  placeholder: t`Start typing to search`
                }}
                onChange={(currentNode, selectedNodes) => {
                  const id = selectedSearchType === 'projects' ? values[1] : values[0]
                  onChange(currentNode, selectedNodes, id)
                }}
                hideIcon
                hideDropDown
              />
            </NutsSection>
            <NutsSection>
              <ButtonContainer>
                <MapButton
                  size="small"
                  onClick={() =>
                    fetchSelectedNuts(
                      selectedSearchType === 'projects' ? selectedPartners : selectedLeadPartners,
                      selectedSearchType === 'projects' ? values[1] : values[0]
                    )
                  }
                >
                  {t`Or choose countries/regions on map`}{' '}
                  <MarkerIcon className="far fa-map-marker-alt" />
                </MapButton>
              </ButtonContainer>
              <NutsDropdown type={type} values={values} />
            </NutsSection>
          </>
        )}
        <Button size="medium" onClick={onClose}>
          {t`Close`}
        </Button>
      </SearchFilter>
      <div>{renderSelectedValues()}</div>
      <NutsModal
        modalOpen={nutsLeadPartnerModalOpen}
        toggleModal={() => setNutsLeadPartnerModalOpen(prevState => !prevState)}
        setNutsFilter={nuts => setNutsFilter(nuts, values[0])}
        selectedValues={selectedLeadPartners}
        disableNutsSelect={false}
      />
      <NutsModal
        modalOpen={nutsPartnerModalOpen}
        toggleModal={() => setNutsPartnerModalOpen(prevState => !prevState)}
        setNutsFilter={nuts => setNutsFilter(nuts, values[1])}
        selectedValues={selectedPartners}
        disableNutsSelect={false}
      />
    </>
  )
})

MultiNutsSelector.propTypes = {
  type: PropTypes.string,
  filterData: PropTypes.object,
  values: PropTypes.array,
  title: PropTypes.string,
  onClose: PropTypes.func,
  showFilter: PropTypes.bool,
  customTop: PropTypes.string
}

PureMultiNutsSelector.propTypes = {
  type: PropTypes.string,
  filterData: PropTypes.object,
  values: PropTypes.array,
  title: PropTypes.string,
  onClose: PropTypes.func,
  showFilter: PropTypes.bool,
  customTop: PropTypes.string,
  filterObject: PropTypes.object,
  regionsByStrategy: PropTypes.object,
  regions: PropTypes.array
}

export default MultiNutsSelector
